'use client'

import * as React from 'react'
import { ArrowDownNarrowWide, ArrowUpNarrowWide, Search } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Toolbar — standardized list-page toolbar.
 * Figma: Etz Design System / Page Header & Toolbar / Toolbar (449:525).
 *
 * Anatomy: [tabs] | [search · filter chips · sort]. Renders as a single row on
 * desktop with a vertical rule after the tabs; below lg the tabs become their
 * own full-bleed bordered row and the controls wrap beneath them; below sm the
 * tabs split the full width evenly and the search stretches full width.
 * Designed to sit inside PageHeader's `toolbar` slot, which owns the outer
 * padding (the tabs row bleeds through it via negative margins).
 */

interface ToolbarProps {
    /** ToolbarTab elements; omit entirely on pages without view tabs */
    tabs?: React.ReactNode
    /** Controls: ToolbarSearch, FilterChips, ToolbarSort, selects, etc. */
    children?: React.ReactNode
    className?: string
}

export function Toolbar({ tabs, children, className }: ToolbarProps) {
    return (
        <div className={cn('flex flex-col lg:flex-row lg:items-center lg:gap-4', className)}>
            {tabs && (
                <>
                    {/* Segmented pill track (reskin 2026-07-31). Scrolls
                        horizontally on narrow viewports — never wraps. */}
                    <div className="max-w-full overflow-x-auto lg:shrink-0">
                        <div className="inline-flex items-center gap-1 rounded-lg bg-[var(--bg-surface)] p-1 ring-1 ring-[var(--border-subtle)]">
                            {tabs}
                        </div>
                    </div>
                    {children && <ToolbarDivider className="hidden lg:block" />}
                </>
            )}
            {children && (
                <div className={cn(
                    'flex min-w-0 flex-wrap items-center gap-3 lg:flex-1 lg:flex-nowrap lg:gap-4',
                    tabs && 'pt-3 lg:pt-0'
                )}>
                    {children}
                </div>
            )}
        </div>
    )
}

interface ToolbarTabProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    active?: boolean
}

/**
 * Segmented filter pill (reskin 2026-07-31 — replaces the underline tab).
 * Visual pill is 36px inside the track; the before: pseudo-element pads the
 * hit area back to the 44px touch-target rule. Active = sage tint with
 * text-safe sage (never sage-500 as text — sub-AA).
 */
export function ToolbarTab({ active, className, children, ...props }: ToolbarTabProps) {
    return (
        <button
            type="button"
            aria-pressed={active ?? false}
            className={cn(
                'relative inline-flex h-9 shrink-0 items-center justify-center rounded-md px-3.5 text-sm font-semibold leading-5 whitespace-nowrap transition-colors',
                "before:absolute before:-inset-y-1 before:inset-x-0 before:content-['']",
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--border-focus)]',
                active
                    ? 'bg-[var(--sage-50)] text-[var(--text-sage)] ring-1 ring-[var(--sage-200)]'
                    : 'text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)] hover:text-[var(--text-primary)]',
                className
            )}
            {...props}
        >
            {children}
        </button>
    )
}

interface ToolbarSearchProps extends React.InputHTMLAttributes<HTMLInputElement> {
    /** className styles the outer field wrapper, not the input */
    className?: string
}

/** Search field with leading icon. Full width below sm, flexes to fill on desktop. */
export const ToolbarSearch = React.forwardRef<HTMLInputElement, ToolbarSearchProps>(
    ({ className, placeholder, 'aria-label': ariaLabel, ...props }, ref) => {
        return (
            <div className={cn(
                'flex h-10 w-full min-w-0 items-center gap-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-3 transition-shadow',
                'focus-within:ring-2 focus-within:ring-[var(--border-focus)] focus-within:border-[var(--border-focus)]',
                'sm:w-auto sm:flex-1 sm:min-w-48',
                className
            )}>
                <Search className="size-4 shrink-0 text-[var(--icon-tertiary)]" aria-hidden="true" />
                <input
                    ref={ref}
                    type="search"
                    placeholder={placeholder}
                    // A placeholder is not an accessible name (it vanishes on input);
                    // fall back to it only when the caller gave nothing better.
                    aria-label={ariaLabel ?? placeholder}
                    className="w-full min-w-0 bg-transparent text-sm leading-5 text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none"
                    {...props}
                />
            </div>
        )
    }
)
ToolbarSearch.displayName = 'ToolbarSearch'

/** Vertical rule between toolbar zones (Etz: 20px, border/subtle). */
export function ToolbarDivider({ className }: { className?: string }) {
    return <div aria-hidden="true" className={cn('h-5 w-px shrink-0 bg-[var(--border-subtle)]', className)} />
}

interface ToolbarSortProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    /** Current sort field label, e.g. "Name" */
    value: string
    direction?: 'asc' | 'desc'
}

/**
 * Sort readout ("Sort: Name ↑"). Only for pages that already expose a sort;
 * wire onClick to toggle direction.
 */
export function ToolbarSort({ value, direction = 'asc', className, ...props }: ToolbarSortProps) {
    const Icon = direction === 'asc' ? ArrowUpNarrowWide : ArrowDownNarrowWide
    return (
        <button
            type="button"
            className={cn(
                'inline-flex min-h-8 shrink-0 items-center gap-1.5 rounded-md px-1.5 text-xs leading-4 text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]',
                "relative before:absolute before:inset-x-0 before:-inset-y-1.5 before:content-['']",
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]',
                className
            )}
            {...props}
        >
            <span>Sort: {value}</span>
            <Icon className="size-3.5" aria-hidden="true" />
            <span className="sr-only">{direction === 'asc' ? 'ascending' : 'descending'}, activate to reverse</span>
        </button>
    )
}
